/**
 * HOW MANY OF A BLOCK AN EMAIL MAY HOLD — read from the block, not from a list here.
 *
 * Layne, `MAXCOUNT-NOT-READ-REPORT.md` (2026-08-26): she added `maxCount` to the
 * `@GUARDRAILS` header of all 26 content blocks and duplication behaviour did not change
 * for any of them. Her decisive test: `ECH_SocialIcons` caps at one, she set
 * `maxCount: 2`, and the second copy was still refused. A second test ruled out the
 * `module` value as the key — she pointed `ECH_MainImage600` at module `mainImage` and it
 * still duplicated freely.
 *
 * Both results are explained by `singletonBlocks.ts`: enforcement came from a hardcoded
 * set of nine block names, so the nine were capped no matter what their header said and
 * the other seventeen were uncapped no matter what their header said. This module is the
 * fix — the cap is whatever the block declares.
 *
 * THREE THINGS THAT FOLLOW, all from her report:
 *   • a new block is governed the moment it exists. `ECH_MainImage600` needed no code
 *     change to be created and needs none to be capped.
 *   • non-1 limits are expressible. `ECH_Featured1CTA`/`ECH_Featured2CTA` declare 5 and
 *     `ECH_DividerLine` declares 4; the old list could only say "one" or "unlimited".
 *   • the limit sits next to the rest of the block's rules, where anyone looking for it
 *     would expect it.
 *
 * CROSS-BLOCK RULES CANNOT LIVE IN ONE HEADER, so the template declares those. All seven
 * `ECH_TPL_*` blocks carry a `rules[]`, read verbatim off the live library 2026-08-26:
 *   mainimage-max-1  maxCombinedCount 1 across ECH_MainImage + ECH_MainImage600
 *   cta-max-1        maxCombinedCount 1 across ECH_1CTA + ECH_2CTA
 *   featured-max-5   maxCombinedCount 5 across ECH_Featured1CTA + ECH_Featured2CTA
 *   featured-variant-exclusive       mutuallyExclusive, the two featured variants
 *   listings-presentation-exclusive  mutuallyExclusive, ListingTable vs featured
 * `mustImmediatelyPrecede` and `interchangeable` also appear there. They are ORDERING and
 * SUBSTITUTION rules, not counts, so a placement check is the wrong place to enforce them
 * — they are deliberately ignored here rather than approximated.
 *
 * THE FALLBACK IS THE OLD LIST, AND IT IS NOT DEAD CODE. A block whose header declares no
 * `maxCount` — an un-annotated block, or any doc holding hand-built repo types (`hero`,
 * `logo`) before an SFMC pull lands — still caps at one if the old list named it. Removing
 * that would silently UNCAP the nine blocks that work today, which is the one regression
 * this change must not ship.
 *
 * WHAT IS STILL NOT SOLVED (her open question): `ECH_DividerLine`'s real rule is "one
 * fewer than the number of featured modules", which is neither a per-block count nor a
 * combined cap. Its declared 4 is enforced here as a ceiling — better than unlimited,
 * still not the rule. Her preferred fix is to fold the divider into the bottom of the
 * featured module and have the assembler omit it on the last instance.
 */
import { echByName, echMeta, echNameOf } from "./echRegistry";
import { echTplRules, type EchTemplateRule } from "./echTemplates";
import { isSingletonType } from "./singletonBlocks";

/** the shape a placement check needs off a document — `type` is all of it */
export interface CapBlock { type: string }

/**
 * How many of `type` an email may hold. 0 means uncapped.
 *
 * The DECLARED value wins outright, including a declared value that is larger than the
 * old list's implicit one — that is the whole point of reading the header, and it is what
 * makes her `maxCount: 2` test pass.
 */
export function capOfBlockType(type: string): number {
  const t = String(type || "");
  if (!t) return 0;
  const m = echMeta(t);
  const declared = m && m.full ? m.full.maxCount : undefined;
  if (typeof declared === "number" && isFinite(declared) && declared > 0) return Math.floor(declared);
  /* no declared cap → the pre-2026-08-26 behaviour, so the nine that work keep working */
  return isSingletonType(t) ? 1 : 0;
}

/**
 * A block that declares `maxCount: 0` can never be ADDED by an author — it is template
 * furniture that arrives with the template (Layne, ASK-JACK-DISABLE-CONTROLS-20260903:
 * "ECH_FeaturedDivider declares maxCount: 0 … nothing about it should be offerable").
 * `capOfBlockType` reads 0 as "uncapped" for the blocks that declare nothing, so the
 * literal zero has to be read separately, here.
 */
export function neverAddable(type: string): boolean {
  const m = echMeta(String(type || ""));
  const declared = m && m.full ? m.full.maxCount : undefined;
  return declared === 0;
}

/**
 * THE RULE'S OWN SENTENCE, WHEN IT FITS (2026-09-04).
 *
 * Layne rewrote all 80 author-facing messages to one sentence each on 2026-09-03
 * (longest 78 characters — `mainimage-max-1` is now "Only one Main Image per email."),
 * precisely so they could be shown on the control. Until then they were maintainer prose
 * (189–430 characters) and this module wrote its own line instead. The declared sentence
 * now wins when it is tooltip-sized; a long one still falls back to the generated line,
 * so an older library never puts a paragraph on a button.
 */
const TOOLTIP_MAX = 120;
function ruleSentence(rules: EchTemplateRule[], echName: string, types: string[], fallback: string): string {
  const want = echName.toLowerCase();
  for (const r of rules) {
    if (types.indexOf(String(r.type || "")) < 0) continue;
    if (!(r.applies || []).some((a) => String(a || "").toLowerCase() === want)) continue;
    const m = String(r.message || "").trim();
    if (m && m.length <= TOOLTIP_MAX) return m;
  }
  return fallback;
}

/** one rule's own sentence when it is tooltip-sized, else the generated fallback */
function ownSentence(r: EchTemplateRule, fallback: string): string {
  const m = String(r.message || "").trim();
  return m && m.length <= TOOLTIP_MAX ? m : fallback;
}

/** the SFMC block name for a doc block's type, or "" for a hand-built repo type */
function nameOf(type: string): string {
  return echNameOf(String(type || "")) || "";
}

/** how many blocks in `doc` carry any of these SFMC names */
function countNamed(doc: CapBlock[] | null | undefined, names: string[]): number {
  if (!names.length) return 0;
  const want = new Set(names.map((n) => n.toLowerCase()));
  let n = 0;
  for (const b of doc || []) {
    if (!b) continue;
    const nm = nameOf(b.type).toLowerCase();
    if (nm && want.has(nm)) n++;
  }
  return n;
}

/** which of these SFMC names the doc already holds */
function presentNamed(doc: CapBlock[] | null | undefined, names: string[]): string[] {
  const have = new Set<string>();
  const want = new Map(names.map((n) => [n.toLowerCase(), n]));
  for (const b of doc || []) {
    if (!b) continue;
    const hit = want.get(nameOf(b.type).toLowerCase());
    if (hit) have.add(hit);
  }
  return [...have];
}

/**
 * What to CALL a block in a refusal.
 *
 * The block's DECLARED label first — Layne writes those and they read like English
 * ("CTA Buttons (2 Up)", "Hero Headline Image", "Body Copy"). Splitting the SFMC name
 * instead produced "an email only has one 2 cta", measured in the live Studio.
 * Parentheticals are dropped because they are dimensions and variant notes, not the
 * thing's name: "Main Image (500x350)" → "main image".
 *
 * Falls back to splitting the name for a block with no label, which is the only reason
 * the split is still here.
 */
function words(echName: string): string {
  const n = String(echName || "");
  if (!n) return "";
  const hit = echByName(n);
  /* `full` is the block's whole rule set; `gr` is the caps-only subset, which may or may
     not carry the label depending on how it was built — read both rather than assume. */
  const lab = String((hit && hit.full && hit.full.label) || (hit && hit.gr && hit.gr.label) || "");
  const src = lab || n.replace(/^ECH_/i, "").replace(/([a-z0-9])([A-Z])/g, "$1 $2");
  return src.replace(/\s*\([^)]*\)/g, "").toLowerCase().trim();
}

/**
 * Why placing another `type` is refused, or null when it is allowed.
 *
 * Counts what the DOCUMENT already holds rather than trusting a flag, so it stays correct
 * after a template load, a duplicated job or an SFMC re-pull — none of which go through a
 * placement surface, and any of which can seed a doc that is already at its cap.
 *
 * `tplName` is the template being assembled from ("" for a bespoke document). Without it
 * the per-block caps still apply and only the cross-block rules go unchecked.
 */
export function capViolation(
  doc: CapBlock[] | null | undefined,
  type: string,
  tplName?: string,
): string | null {
  const t = String(type || "");
  if (!t) return null;
  const d = doc || [];
  const echName = nameOf(t);
  const rules: EchTemplateRule[] = echTplRules(tplName || "");

  /* 0 — declared `maxCount: 0`: never offerable at all */
  if (neverAddable(t)) {
    const label = words(echName) || "block";
    return ruleSentence(rules, echName, ["maxCount"], `${label[0].toUpperCase() + label.slice(1)} comes with the template and can't be added.`);
  }

  /* 1 — the block's own declared cap */
  const cap = capOfBlockType(t);
  if (cap > 0) {
    const mine = d.filter((b) => b && b.type === t).length;
    if (mine >= cap) {
      const label = words(echName) || "block";
      return ruleSentence(rules, echName, ["maxCount"], cap === 1
        ? `An email only has one ${label} — edit the one on the canvas`
        : `Up to ${cap} ${label} blocks — remove one to add another`);
    }
  }

  /* 2 — the template's cross-block rules */
  if (!echName) return null;
  for (const r of rules) {
    const applies = r.applies || [];
    if (!applies.some((a) => a.toLowerCase() === echName.toLowerCase())) continue;

    /* the sentence comes from THE RULE THAT FIRED (2026-10-07). `ruleSentence` returns the
       first rule of the same type naming this block, and on a template with two
       mutuallyExclusive rules on the Featured blocks (`featured-variant-exclusive` before
       `listings-presentation-exclusive`), a Featured chip greyed by the Listing Table read
       "don't mix 1-button and 2-button", measured in the M2 panel on Now Selling. */
    if (r.type === "maxCombinedCount" && typeof r.max === "number" && r.max > 0) {
      if (countNamed(d, applies) >= r.max) {
        const pair = applies.map(words).join(" or ");
        return ownSentence(r, r.max === 1
          ? `An email has one ${pair} — edit the one on the canvas`
          : `Up to ${r.max} ${pair} — remove one to add another`);
      }
    }

    if (r.type === "mutuallyExclusive") {
      const other = presentNamed(d, applies).filter((n) => n.toLowerCase() !== echName.toLowerCase());
      if (other.length) {
        return ownSentence(r, `This email already uses ${words(other[0])} — the two can't be mixed`);
      }
    }
  }
  return null;
}

/** kept for the two placement surfaces that only need yes/no */
export function wouldExceedCap(doc: CapBlock[] | null | undefined, type: string, tplName?: string): boolean {
  return capViolation(doc, type, tplName) != null;
}
